import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Plus, StickyNote } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { fetchUserNotes, addUserNote, type UserNote } from './userNotesApi'

/**
 * A3.19 (ko'rish) + A3.38 (qo'shish) — ichki xodim izohlari. Moderatorlar
 * foydalanuvchi haqida DURABLE, append-only kontekst qoldiradi ("spam uchun
 * ogohlantirildi") — keyingi moderator tarixni ko'radi. Audit'дан (AMALLAR) va
 * ogohlantirishдан (foydalanuvchiga) farqli: bu MAXFIY xodim eslatmasi.
 * Matnlar lokal (i18n HOT fayllarга tegilmaydi).
 */

const S = {
  title: 'Ichki izohlar',
  back: 'Karta',
  placeholder: "Foydalanuvchi haqida ichki izoh (faqat xodimlar ko'radi)…",
  add: "Qo'shish",
  added: "Izoh qo'shildi",
  empty: 'Hozircha izoh yo‘q',
  failed: "Izoh qo'shilmadi",
  by: 'Muallif',
  note: 'Izoh',
  appendOnly: "Izohlar o'chirilmaydi — hisobdorlik uchun append-only.",
}

function fmtTime(ts: number): string {
  if (!Number.isFinite(ts)) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return '—'
  }
}

export function UserNotes() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [notes, setNotes] = useState<UserNote[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    fetchUserNotes(userId, 100)
      .then(setNotes)
      .catch(() => setNotes([]))
      .finally(() => setLoading(false))
  }, [userId])

  useEffect(() => {
    load()
  }, [load])

  const add = useCallback(async () => {
    const text = draft.trim()
    if (!text) return
    setSaving(true)
    try {
      await addUserNote(userId, text)
      toast(S.added, { tone: 'success' })
      setDraft('')
      load()
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setSaving(false)
    }
  }, [draft, userId, toast, load])

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A3.19"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-2xl space-y-3">
          {/* Qo'shish formasi (A3.38) */}
          <div className="rounded-lg border border-border-default bg-surface-2 p-3">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={S.placeholder}
              rows={3}
              maxLength={4000}
              className="w-full resize-y rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-border-strong"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-tertiary">{S.appendOnly}</span>
              <button
                onClick={() => void add()}
                disabled={saving || !draft.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-50"
              >
                <Plus size={14} />
                {S.add}
              </button>
            </div>
          </div>

          {/* Ro'yxat (A3.19) */}
          {notes === null || loading ? (
            <div className="p-6 text-center text-sm text-tertiary">…</div>
          ) : notes.length === 0 ? (
            <div className="px-3 py-10 text-center text-sm text-tertiary">
              <StickyNote size={24} className="mx-auto mb-2 opacity-60" />
              {S.empty}
            </div>
          ) : (
            <ul className="space-y-2">
              {notes.map((n) => (
                <li key={n.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="mb-1 flex items-center justify-between text-xs text-tertiary">
                    <span>{n.author ? `@${n.author}` : '—'}</span>
                    <span>{fmtTime(n.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm text-primary">{n.note}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  )
}
